import React from 'react';
import { Page, View, Text, StyleSheet, Link } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface ScopePageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2563EB',
    marginBottom: 8,
    marginTop: 12,
  },
  scopeMainTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
    marginTop: 16,
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 14,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 8,
    color: '#475569',
    fontSize: 11.5,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 28,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bullet: {
    fontSize: 11.5,
    color: '#4B5563',
    marginRight: 6,
  },
  bulletText: {
    fontSize: 11.5,
    color: '#4B5563',
    lineHeight: 1.60,
    flex: 1,
  },
});

const ScopePageV2: React.FC<ScopePageProps> = ({ reportData, sectionId }) => {
  const targets = reportData.target_details || [];
  const hasUserRoles = targets.some((t: any) => 
    (t.roles && Array.isArray(t.roles) && t.roles.some((r: any) => r.role || r.privilege_level)) || 
    (t.role || t.privilege_level)
  );
  
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isMixed = webTargets.length > 0 && networkTargets.length > 0;

  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  let parsedConfig: any = { globalFields: { description: true, instructions: true, impact: true, fix: true, evidence: true }, individualFindings: {} };
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const config = parsedConfig;

  const uniqueHostsWithFindings = new Set(
    (reportData.detailed_findings || [])
      .map((f: any) => (f.scope || '').trim())
      .filter(Boolean)
  ).size;

  const dynamicTotalAddresses = networkTargets.length || targets.length || 0;
  const dynamicLiveHosts = networkTargets.length || targets.length || 0;
  const dynamicHostsWithFindings = uniqueHostsWithFindings;
  const dynamicPosition = "External & Internal Network Security Assessment";

  const networkSummary = {
    totalAddresses: config.networkScopeSummary?.totalAddresses || String(dynamicTotalAddresses),
    liveHosts: config.networkScopeSummary?.liveHosts || String(dynamicLiveHosts),
    hostsWithFindings: config.networkScopeSummary?.hostsWithFindings || String(dynamicHostsWithFindings),
    position: config.networkScopeSummary?.position || dynamicPosition
  };

  const recs = Array.isArray(reportData.recommendations_list) && reportData.recommendations_list.length > 0
    ? reportData.recommendations_list
    : [
        { title: 'Short (0-30 days)', description: 'Remediate critical and high severity findings; deploy WAF virtual patches.' },
        { title: 'Medium (1-3 mo)', description: 'Add SAST/DAST to CI/CD and centralize input validation.' },
        { title: 'Long (3-12 mo)', description: 'Establish an SDLC security program with recurring pentests.' }
      ];

  const renderRecommendationsTable = () => (
    <View style={styles.table}>
      <View style={styles.tableHeader}>
        <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Horizon</Text>
        <Text style={[styles.tableHeaderCell, { width: '70%' }]}>Recommended Action</Text>
      </View>
      {recs.map((rec: any, idx: number) => {
        const isLast = idx === recs.length - 1;
        return (
          <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
            <Text style={[styles.tableRowCell, { width: '30%', fontWeight: 'bold', color: '#1F2937' }]}>
              {rec.title}
            </Text>
            <Text style={[styles.tableRowCell, { width: '70%', color: '#4B5563' }]}>
              {rec.description}
            </Text>
          </View>
        );
      })}
    </View>
  );

  const renderScopeTable = (targetList: any[], renderAsNetwork: boolean) => {
    const listToRender = targetList || [];
    if (listToRender.length === 0) {
      return (
        <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
          <Text style={[styles.tableRowCell, { width: '100%', textAlign: 'center', color: '#9CA3AF' }]}>
            No in-scope targets specified
          </Text>
        </View>
      );
    }
    
    const renderCellUrl = (val: string, widthStyle: any) => {
      const isUrl = val.startsWith('http://') || val.startsWith('https://') || val.includes('.com') || val.includes('.xyz') || val.includes('.org') || val.includes('.net');
      
      if (isUrl) {
        const absoluteUrl = val.startsWith('http://') || val.startsWith('https://') ? val : `https://${val}`;
        return (
          <Link src={absoluteUrl} style={[styles.tableRowCell, widthStyle, { color: '#2563EB', textDecoration: 'underline' }]}>
            {val}
          </Link>
        );
      }
      
      return (
        <Text style={[styles.tableRowCell, widthStyle]}>
          {val}
        </Text>
      );
    };

    if (renderAsNetwork) {
      const subnetTargets = listToRender.filter((t: any) => {
        const val = (t.target_value || t.url || '').toLowerCase();
        return val.includes('/') || val.includes('-');
      });
      const individualIpTargets = listToRender.filter((t: any) => {
        const val = (t.target_value || t.url || '').toLowerCase();
        return !val.includes('/') && !val.includes('-');
      });

      if (subnetTargets.length > 0) {
        return subnetTargets.map((target: any, idx: number) => {
          const isLast = idx === subnetTargets.length - 1;
          const subnet = target.target_type || target.type || 'Network Scope';
          const desc = target.target_value || target.url || '';
          const addressable = target.description || 'Authorized Scope';
          return (
            <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
              {renderCellUrl(desc, { width: '35%', color: '#374151' })}
              <Text style={[styles.tableRowCell, { width: '35%', color: '#374151' }]}>{subnet}</Text>
              <Text style={[styles.tableRowCell, { width: '30%', color: '#4B5563' }]}>{addressable}</Text>
            </View>
          );
        });
      } else if (individualIpTargets.length > 0) {
        return (
          <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
            <Text style={[styles.tableRowCell, { width: '35%', fontWeight: 'bold', color: '#1F2937' }]}>Assessed Hosts</Text>
            <Text style={[styles.tableRowCell, { width: '35%', color: '#4B5563' }]}>Individual Target IPs (See Appendix A)</Text>
            <Text style={[styles.tableRowCell, { width: '30%', color: '#4B5563', fontWeight: 'bold' }]}>{individualIpTargets.length} Hosts</Text>
          </View>
        );
      }
    }

    // Deduplicate web targets by URL
    const seen = new Set<string>();
    const uniqueWebTargets = listToRender.filter((target: any) => {
      const val = (target.url || target.target_value || target.target_name || '').trim().toLowerCase();
      if (!val) return true;
      if (seen.has(val)) return false;
      seen.add(val);
      return true;
    });

    return uniqueWebTargets.map((target: any, idx: number) => {
      const isLast = idx === uniqueWebTargets.length - 1;
      const value = target.url || target.target_value || target.target_name || '';
      const description = target.description || target.target_type || target.type || 'Web Application';
      const techStack = target.tech_stack || target.technology || '\u2014';
      return (
        <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
          {renderCellUrl(value, { width: '40%', color: '#1F2937' })}
          <Text style={[styles.tableRowCell, { width: '30%' }]}>{description}</Text>
          <Text style={[styles.tableRowCell, { width: '30%', color: '#4B5563' }]}>{techStack}</Text>
        </View>
      );
    });
  };

  const renderUserRolesTable = () => {
    const rolesList: { role: string; privilege_level: string; credentials: string }[] = [];
    
    targets.forEach((t: any) => {
      if (t.roles && Array.isArray(t.roles) && t.roles.length > 0) {
        t.roles.forEach((r: any) => {
          if (r.role || r.privilege_level) {
            rolesList.push({
              role: r.role || 'N/A',
              privilege_level: r.privilege_level || 'N/A',
              credentials: r.credentials || 'N/A'
            });
          }
        });
      } else if (t.role || t.privilege_level) {
        rolesList.push({
          role: t.role || 'N/A',
          privilege_level: t.privilege_level || 'N/A',
          credentials: t.credentials || 'N/A'
        });
      }
    });

    if (rolesList.length > 0) {
      return rolesList.map((r: any, idx: number) => {
        const isLast = idx === rolesList.length - 1;
        return (
          <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
            <Text style={[styles.tableRowCell, { width: '35%', fontWeight: 'bold', color: '#1F2937' }]}>{r.role}</Text>
            <Text style={[styles.tableRowCell, { width: '35%' }]}>{r.privilege_level}</Text>
            <Text style={[styles.tableRowCell, { width: '30%', color: '#4B5563' }]}>{r.credentials}</Text>
          </View>
        );
      });
    }
    const defaultRoles = [
      { role: 'Unauthenticated', privilege: 'Anonymous', credentials: 'N/A' },
      { role: 'Standard user', privilege: 'Low', credentials: 'Provided by client' },
      { role: 'Administrator', privilege: 'High', credentials: 'Provided by client' },
    ];
    return defaultRoles.map((r, idx) => {
      const isLast = idx === defaultRoles.length - 1;
      return (
        <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
          <Text style={[styles.tableRowCell, { width: '35%', fontWeight: 'bold', color: '#1F2937' }]}>{r.role}</Text>
          <Text style={[styles.tableRowCell, { width: '35%' }]}>{r.privilege}</Text>
          <Text style={[styles.tableRowCell, { width: '30%', color: '#4B5563' }]}>{r.credentials}</Text>
        </View>
      );
    });
  };

  const oosDefaults = [
    'Denial-of-Service / load testing against production.',
  ];
  const oosBullets: string[] = reportData.scope_notes
    ? reportData.scope_notes.replace(/<[^>]*>/g, '').split('\n').map((s: string) => s.trim()).filter(Boolean)
    : oosDefaults;

  if (isMixed) {
    return (
      <Page size="A4" id={sectionId} style={styles.page}>
        {/* Strategic Recommendations Section */}
        <Text style={styles.sectionTitle}>Strategic recommendations</Text>
        {renderRecommendationsTable()}

        {/* Title: 2. Scope */}
        <Text style={styles.scopeMainTitle}>2. Scope</Text>
        <View style={styles.titleUnderlineRow}>
          <View style={styles.titleUnderlineBlue} />
          <View style={styles.titleUnderlineDark} />
        </View>

        <Text style={styles.sectionTitle}>In-scope application(s)</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: '40%' }]}>Application / URL</Text>
            <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Description</Text>
            <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Tech Stack</Text>
          </View>
          {renderScopeTable(webTargets, false)}
        </View>

        {hasUserRoles && (
          <>
            <Text style={styles.sectionTitle}>User roles tested</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, { width: '35%' }]}>Role</Text>
                <Text style={[styles.tableHeaderCell, { width: '35%' }]}>Privilege Level</Text>
                <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Credentials</Text>
              </View>
              {renderUserRolesTable()}
            </View>
          </>
        )}

        <Text style={styles.sectionTitle}>In-Scope Ranges / Subnets</Text>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: '35%' }]}>SUBNET / RANGE</Text>
            <Text style={[styles.tableHeaderCell, { width: '35%' }]}>DESCRIPTION</Text>
            <Text style={[styles.tableHeaderCell, { width: '30%' }]}>ADDRESSABLE</Text>
          </View>
          {renderScopeTable(networkTargets, true)}
        </View>

        <View style={[styles.table, { marginTop: 4 }]}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Total addresses in scope</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.totalAddresses || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Live hosts discovered</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.liveHosts || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Hosts with findings</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.hostsWithFindings || 'N/A'}</Text>
          </View>
          <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Position</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.position || 'N/A'}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Out of scope / exclusions</Text>
        <View style={{ marginTop: 2 }}>
          {oosBullets.map((bullet: string, idx: number) => (
            <View key={idx} style={styles.bulletRow}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{bullet}</Text>
            </View>
          ))}
        </View>
      </Page>
    );
  }

  if (isNetwork) {
    return (
      <Page size="A4" id={sectionId} style={styles.page}>
        {/* Strategic Recommendations Section */}
        <Text style={styles.sectionTitle}>Strategic recommendations</Text>
        {renderRecommendationsTable()}

        {/* Title: 2. Scope */}
        <Text style={styles.scopeMainTitle}>2. Scope</Text>
        <View style={styles.titleUnderlineRow}>
          <View style={styles.titleUnderlineBlue} />
          <View style={styles.titleUnderlineDark} />
        </View>

        <Text style={styles.paragraph}>
          Scope is summarized below as network ranges and counts. The full inventory of live hosts is listed in Appendix A - Host / Asset Inventory.
        </Text>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: '35%' }]}>SUBNET / RANGE</Text>
            <Text style={[styles.tableHeaderCell, { width: '35%' }]}>DESCRIPTION</Text>
            <Text style={[styles.tableHeaderCell, { width: '30%' }]}>ADDRESSABLE</Text>
          </View>
          {renderScopeTable(networkTargets.length > 0 ? networkTargets : targets, true)}
        </View>

        <View style={[styles.table, { marginTop: 4 }]}>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Total addresses in scope</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.totalAddresses || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Live hosts discovered</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.liveHosts || 'N/A'}</Text>
          </View>
          <View style={styles.tableRow}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Hosts with findings</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.hostsWithFindings || 'N/A'}</Text>
          </View>
          <View style={[styles.tableRow, { borderBottomWidth: 0 }]}>
            <Text style={[styles.tableRowCell, { width: '38%', fontWeight: 'bold', color: '#1F2937' }]}>Position</Text>
            <Text style={[styles.tableRowCell, { width: '62%' }]}>{networkSummary.position || 'N/A'}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Out of scope / exclusions</Text>
        <View style={{ marginTop: 2 }}>
          {oosBullets.map((bullet: string, idx: number) => (
            <View key={idx} style={styles.bulletRow}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.bulletText}>{bullet}</Text>
            </View>
          ))}
        </View>
        {/* Footer page number */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
          <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    );
  }

  // Web Layout
  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Strategic Recommendations Section */}
      <Text style={styles.sectionTitle}>Strategic recommendations</Text>
      {renderRecommendationsTable()}

      {/* Title: 2. Scope */}
      <Text style={styles.scopeMainTitle}>2. Scope</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      <Text style={styles.sectionTitle}>In-scope application(s)</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: '40%' }]}>Application / URL</Text>
          <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Description</Text>
          <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Tech Stack</Text>
        </View>
        {renderScopeTable(webTargets.length > 0 ? webTargets : targets, false)}
      </View>

      {hasUserRoles && (
        <>
          <Text style={styles.sectionTitle}>User roles tested</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableHeaderCell, { width: '35%' }]}>Role</Text>
              <Text style={[styles.tableHeaderCell, { width: '35%' }]}>Privilege Level</Text>
              <Text style={[styles.tableHeaderCell, { width: '30%' }]}>Credentials</Text>
            </View>
            {renderUserRolesTable()}
          </View>
        </>
      )}

      <Text style={styles.sectionTitle}>Out of scope / exclusions</Text>
      <View style={{ marginTop: 2 }}>
        {oosBullets.map((bullet: string, idx: number) => (
          <View key={idx} style={styles.bulletRow}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.bulletText}>{bullet}</Text>
          </View>
        ))}
      </View>
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default ScopePageV2;
